import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { SpaceErrorCode } from 'shared';

import { QueryKey } from '../../../constants/index';
import { HttpException } from '../../../exceptions/index';
import { useAppMutation, useInvalidateQueries, useLocale } from '../../../hooks/index';
import { ApiService } from '../../../services/index';
import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import { FOLDER_INVALIDATION_KEYS, SPACE_BACKEND_ROUTE } from '../constants/spaces-query.const';
import { TreeSpaceRef } from '../types/data-room.types';
import { invalidateSpaceFolderLevels } from './use-space-navigation-folders.hook';

type CreateFolderBody = { name: string; parentFolderId: null | string };

type CreateFolderVariables = { space: TreeSpaceRef } & CreateFolderBody;

type Props = {
  onDuplicateName?: () => void;
  onSuccess?: () => void;
};

export const useCreateFolder = ({ onDuplicateName, onSuccess }: Props = {}) => {
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const queryClient = useQueryClient();
  const { messagesLocale } = useLocale();
  const { invalidateQuery } = useInvalidateQueries(...FOLDER_INVALIDATION_KEYS);

  const { isPending, mutate: createFolder } = useAppMutation<
    unknown,
    HttpException,
    CreateFolderVariables
  >([QueryKey.CREATE_SPACE_FOLDER, companyId], {
    // Replaced: a taken name goes to the field, anything else gets a translated toast.
    defaultErrorHandling: false,
    mutationFn: ({ space, ...body }) =>
      ApiService.post<unknown, CreateFolderBody>(
        { endpoint: SPACE_BACKEND_ROUTE[space.kind], routePath: [space.id, 'folders'] },
        { body },
      ),
    onError: (error) => {
      if (error.body?.code === SpaceErrorCode.FOLDER_NAME_TAKEN) {
        onDuplicateName?.();

        return;
      }

      toast.error(messagesLocale['FAILED_TO_SAVE_CHANGES']);
    },
    onSuccess: (_, { space }) => {
      toast.success(messagesLocale['FOLDER_CREATED']);
      invalidateQuery();
      invalidateSpaceFolderLevels(queryClient, companyId, space);
      onSuccess?.();
    },
  });

  return { createFolder, isPending };
};
